import { useEffect, useState } from 'react';
import { averageFps } from '../util/mcpbVideoInfo';

const COMMIT_MS = 500;   // how often the live snapshot is pushed to state (decoupled from paint rate)
const FPS_SAMPLES = 12;  // rolling window of inter-frame samples
const STALE_MS = 1500;   // no painted frame within this → report fps null (paused / buffering)

/**
 * useVideoInfo — read a slot <video>'s live stream stats for the cog "Camera Info overlay" (the
 * desktop DstreamView "Toggle Info" equivalent: Res / FPS / profile / buffer state).
 *
 * Returns { width, height, fps, readyState, profile } while `enabled`, else null — and does NO work
 * when disabled (no rVFC, no interval), so four idle cells cost nothing when the overlay is off. FPS is
 * estimated from requestVideoFrameCallback (Chrome / Edge / Safari); browsers without it (Firefox)
 * report fps null → "— fps" (graceful degrade, David 2026-06-21). To avoid a re-render per painted
 * frame, rVFC only mutates a local accumulator; a COMMIT_MS interval snapshots width/height/readyState
 * (read live off the element) plus the rolling fps into state. Pure DOM glue — headed-verified (jsdom
 * has no media pipeline); the formatting + averaging it feeds is unit-tested in util/mcpbVideoInfo.
 *
 * @param {object} videoRef  React ref to the <video> element (owned by Cell)
 * @param {object} opts
 * @param {boolean} opts.enabled  the global cog toggle AND this cell has a clip
 * @param {number} opts.profile   the transcode profile requested for this cell (1 native / 2 down-res)
 */
export const useVideoInfo = (videoRef, { enabled = false, profile = 1 } = {}) => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!enabled) { setInfo(null); return undefined; }
    const v = videoRef && videoRef.current;
    if (!v) { setInfo(null); return undefined; }

    const now = () => ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now());
    const fps = { samples: [], lastFrameAt: 0 };
    let cancelled = false;
    let rvfcHandle = null;
    const hasRVFC = typeof v.requestVideoFrameCallback === 'function';

    // rVFC fires once per presented frame — accumulate inter-frame fps samples (no setState here).
    const onFrame = (ts) => {
      const t = typeof ts === 'number' ? ts : now();
      if (fps.lastFrameAt) {
        const dt = t - fps.lastFrameAt;
        if (dt > 0) {
          fps.samples.push(1000 / dt);
          if (fps.samples.length > FPS_SAMPLES) fps.samples.shift();
        }
      }
      fps.lastFrameAt = t;
      if (!cancelled && hasRVFC) rvfcHandle = v.requestVideoFrameCallback(onFrame);
    };
    if (hasRVFC) rvfcHandle = v.requestVideoFrameCallback(onFrame);

    const commit = () => {
      if (cancelled) return;
      // fps only when frames are actively painting (rVFC fired recently); else null → "— fps".
      const fresh = fps.lastFrameAt && (now() - fps.lastFrameAt) < STALE_MS;
      setInfo({
        width: v.videoWidth || 0,
        height: v.videoHeight || 0,
        readyState: v.readyState || 0,
        profile,
        fps: fresh ? averageFps(fps.samples) : null,
      });
    };
    commit();                                  // immediate first snapshot
    const timer = setInterval(commit, COMMIT_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
      if (rvfcHandle != null && typeof v.cancelVideoFrameCallback === 'function') {
        v.cancelVideoFrameCallback(rvfcHandle);
      }
    };
  }, [videoRef, enabled, profile]);

  return enabled ? info : null;
};

export default useVideoInfo;
